import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { canAccessPath } from '@/lib/access';
import { usePermissions } from '@/hooks/usePermissions';
import { LoginPage } from '@/pages/auth/LoginPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { StudentsPage } from '@/pages/students/StudentsPage';
import { StudentRegistrationPage } from '@/pages/students/StudentRegistrationPage';
import { StudentViewPage } from '@/pages/students/StudentViewPage';
import { TransferCertificatesPage } from '@/pages/students/TransferCertificatesPage';
import { TransferStudentsPage } from '@/pages/students/TransferStudentsPage';
import { ClassChangeRequestsPage } from '@/pages/students/ClassChangeRequestsPage';
import { TeacherClassChangeRequestsPage } from '@/pages/staff/TeacherClassChangeRequestsPage';
import { ParentsPage } from '@/pages/parents/ParentsPage';
import { PromotionPage } from '@/pages/academics/PromotionPage';
import { AcademicCalendarPage } from '@/pages/academic-calendar/AcademicCalendarPage';
import { VisitorsPage } from '@/pages/visitors/VisitorsPage';
import { VisitsPage } from '@/pages/visitors/VisitsPage';
import { StaffPage } from '@/pages/staff/StaffPage';
import { StaffViewPage } from '@/pages/staff/StaffViewPage';
import { StaffLeavePage } from '@/pages/staff/StaffLeavePage';
import { LeaveReportPage } from '@/pages/staff/LeaveReportPage';
import { BiometricDevicesPage } from '@/pages/biometric-devices/BiometricDevicesPage';
import { AcademicYearsPage } from '@/pages/setup/AcademicYearsPage';
import { ClassesPage } from '@/pages/setup/ClassesPage';
import { SubjectsPage } from '@/pages/setup/SubjectsPage';
import { CustomFieldsPage } from '@/pages/setup/CustomFieldsPage';
import { ClassSubjectsPage } from '@/pages/setup/ClassSubjectsPage';
import { LessonsPage } from '@/pages/lessons/LessonsPage';
import { SectionTeachersPage } from '@/pages/setup/SectionTeachersPage';
import { AttendancePage } from '@/pages/attendance/AttendancePage';
import { ViewAttendancePage } from '@/pages/attendance/ViewAttendancePage';
import { AttendanceSummaryPage } from '@/pages/attendance/AttendanceSummaryPage';
import { StaffAttendancePage } from '@/pages/attendance/StaffAttendancePage';
import { TeacherAttendanceSummaryPage } from '@/pages/attendance/TeacherAttendanceSummaryPage';
import { AdminAttendancePage } from '@/pages/attendance/AdminAttendancePage';
import { ExamsPage } from '@/pages/exams/ExamsPage';
import { AssignmentsPage } from '@/pages/assignments/AssignmentsPage';
import { CoScholasticPage } from '@/pages/co-scholastic/CoScholasticPage';
import { ProgramCandidatesPage } from '@/pages/program-candidates/ProgramCandidatesPage';
import { OnlineExamsPage } from '@/pages/online-exams/OnlineExamsPage';
import { QuestionBankPage } from '@/pages/online-exams/QuestionBankPage';
import { NotificationsPage } from '@/pages/notifications/NotificationsPage';
import { AnnouncementsPage } from '@/pages/announcements/AnnouncementsPage';
import { SlidersPage } from '@/pages/sliders/SlidersPage';
import { AffidavitsPage } from '@/pages/affidavits/AffidavitsPage';
import { ReportCardPage } from '@/pages/report-cards/ReportCardPage';
import { TimetablePage, TeacherTimetablePage } from '@/pages/timetable/TimetablePage';
import { FeesPage } from '@/pages/fees/FeesPage';
import { FeesAdjustmentPage } from '@/pages/fees/FeesAdjustmentPage';
import { FeesDemandsPage } from '@/pages/fees/FeesDemandsPage';
import { FeeDemandFormPage } from '@/pages/fees/FeeDemandFormPage';
import { NooraniGroupsPage } from '@/pages/noorani-groups/NooraniGroupsPage';
import { NooraniAttendanceReportPage } from '@/pages/noorani-groups/NooraniAttendanceReportPage';
import { NooraniTeacherAttendancePage } from '@/pages/noorani-groups/NooraniTeacherAttendancePage';
import { NooraniTutorialsPage } from '@/pages/noorani-groups/NooraniTutorialsPage';
import { HoFeesSharePage } from '@/pages/fees/HoFeesSharePage';
import { OrgSchoolFeesSummaryPage } from '@/pages/org/OrgSchoolFeesSummaryPage';
import { OrgHeadOfficeFeesOverviewPage } from '@/pages/org/OrgHeadOfficeFeesOverviewPage';
import { OrgSlidersPage } from '@/pages/org/OrgSlidersPage';
import { OrgNotificationsPage } from '@/pages/org/OrgNotificationsPage';
import { OrgFeeTypesPage } from '@/pages/org/OrgFeeTypesPage';
import { QuranTargetsPage } from '@/pages/quran-tracker/QuranTargetsPage';
import { QuranStudentTargetsPage } from '@/pages/quran-tracker/QuranStudentTargetsPage';
import { NooraniFeesPage } from '@/pages/fees/NooraniFeesPage';
import { NooraniFeeReportPage } from '@/pages/fees/NooraniFeeReportPage';
import { ExpenseCategoriesPage } from '@/pages/expenses/ExpenseCategoriesPage';
import { ExpensesPage } from '@/pages/expenses/ExpensesPage';
import { LoginReportPage } from '@/pages/reports/LoginReportPage';
import { DataExportPage } from '@/pages/reports/DataExportPage';
import { StudentSummaryReportPage } from '@/pages/reports/StudentSummaryReportPage';
import { TeachersReportPage } from '@/pages/reports/TeachersReportPage';
import { FinanceReportPage } from '@/pages/reports/FinanceReportPage';
import { VideoViewReportPage } from '@/pages/reports/VideoViewReportPage';
import { StudentsReportPage as SchoolStudentsReportPage } from '@/pages/reports/StudentsReportPage';
import { StudentPortfolioPage } from '@/pages/reports/StudentPortfolioPage';
import { VerifyIdCardsPage } from '@/pages/id-cards/VerifyIdCardsPage';
import { OnlineAdmissionsPage } from '@/pages/online-admission/OnlineAdmissionsPage';
import { PgcRegistrationsPage } from '@/pages/pgc-registrations/PgcRegistrationsPage';
import { OnlineAdmissionApplyPage } from '@/pages/public/OnlineAdmissionApplyPage';
import { GenerateIdCardsPage } from '@/pages/org/GenerateIdCardsPage';
import { OrgOnlineAdmissionsPage } from '@/pages/org/OrgOnlineAdmissionsPage';
import { OrgOnlineAdmissionsSummaryPage } from '@/pages/org/OrgOnlineAdmissionsSummaryPage';
import { OrgManageOnlineAdmissionClassesPage } from '@/pages/org/OrgManageOnlineAdmissionClassesPage';
import { StudentAttendanceReportPage } from '@/pages/attendance/StudentAttendanceReportPage';
import { DetailedStudentAttendancePage } from '@/pages/attendance/DetailedStudentAttendancePage';
import { TeacherAttendanceReportPage } from '@/pages/attendance/TeacherAttendanceReportPage';
import { DetailedTeacherAttendancePage } from '@/pages/attendance/DetailedTeacherAttendancePage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { SettingsPage as PlatformSettingsPage } from '@/pages/superadmin/SettingsPage';
import { MobileApiDocsPage } from '@/pages/mobile-api-docs/MobileApiDocsPage';
import { UsageHistoryPage } from '@/pages/usage-logs/UsageHistoryPage';
import { UserUsageHistoryPage } from '@/pages/usage-logs/UserUsageHistoryPage';
import { BillingPage } from '@/pages/billing/BillingPage';
import { SchoolLayout } from '@/components/shared/SchoolLayout';
import { PublicLayout } from '@/components/public/PublicLayout';
import { LandingPage } from '@/pages/public/LandingPage';
import { PublicContentPage } from '@/pages/public/PublicContentPage';
import { PricingPage } from '@/pages/public/PricingPage';
import { SignupPage } from '@/pages/public/SignupPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { PagePlaceholder } from '@/pages/_PagePlaceholder';

import { PortalLoginPage } from '@/pages/portal/PortalLoginPage';
import { PortalHomePage } from '@/pages/portal/PortalHomePage';
import { GuardianLoginPage } from '@/pages/guardian/GuardianLoginPage';
import { GuardianChildrenPage } from '@/pages/guardian/GuardianChildrenPage';
import { MisReportsPage } from '@/pages/mis-reports/MisReportsPage';
import { MaterialItemsPage } from '@/pages/material-items/MaterialItemsPage';
import { MaterialRequestsPage } from '@/pages/material-requests/MaterialRequestsPage';
import { ParentAttendancePage } from '@/pages/guardian-attendance/ParentAttendancePage';
import { ParentAttendanceSummaryPage } from '@/pages/guardian-attendance/ParentAttendanceSummaryPage';
import { SuperadminLoginPage } from '@/pages/superadmin/SuperadminLoginPage';
import { SuperadminLayout } from '@/components/superadmin/SuperadminLayout';
import { OrgLoginPage } from '@/pages/org/OrgLoginPage';
import { OrgDashboardPage } from '@/pages/org/OrgDashboardPage';
import { OrgOverviewPage } from '@/pages/org/OrgOverviewPage';
import { OrgAdminUsersPage } from '@/pages/org/OrgAdminUsersPage';
import { OrgAcademicCalendarPage } from '@/pages/org/OrgAcademicCalendarPage';
import { OrgSelectSchoolPage } from '@/pages/org/OrgSelectSchoolPage';
import { StudentsReportPage } from '@/pages/org/StudentsReportPage';
import { OrgDepartmentsPage } from '@/pages/org/OrgDepartmentsPage';
import { OrgFinanceReportPage } from '@/pages/org/OrgFinanceReportPage';
import { OrgLoginReportPage } from '@/pages/org/OrgLoginReportPage';
import { OrgTeachersReportPage } from '@/pages/org/OrgTeachersReportPage';
import { OrgStudentPortfolioPage } from '@/pages/org/OrgStudentPortfolioPage';
import { OrgGuardiansPage } from '@/pages/org/OrgGuardiansPage';
import { OrgPgcRegistrationsPage } from '@/pages/org/OrgPgcRegistrationsPage';
import { OrgAffidavitsManagePage } from '@/pages/org/OrgAffidavitsManagePage';
import { OrgStaffPage } from '@/pages/org/OrgStaffPage';
import { OrgRolesPage } from '@/pages/org/OrgRolesPage';
import { OrgStaffLeavePage } from '@/pages/org/OrgStaffLeavePage';
import { OrgLeaveReportPage } from '@/pages/org/OrgLeaveReportPage';
import { OrgStudentsAttendanceSummaryPage } from '@/pages/org/OrgStudentsAttendanceSummaryPage';
import { OrgTeachersAttendanceSummaryPage } from '@/pages/org/OrgTeachersAttendanceSummaryPage';
import { OrgStudentAttendanceReportPage } from '@/pages/org/OrgStudentAttendanceReportPage';
import { OrgTeacherAttendanceReportPage } from '@/pages/org/OrgTeacherAttendanceReportPage';
import { OrgDetailedStudentAttendancePage } from '@/pages/org/OrgDetailedStudentAttendancePage';
import { OrgDetailedTeacherAttendancePage } from '@/pages/org/OrgDetailedTeacherAttendancePage';
import { OrgStudentSummaryReportPage } from '@/pages/org/OrgStudentSummaryReportPage';
import { OrgReportsDashboardPage } from '@/pages/org/OrgReportsDashboardPage';
import { OrgParentAttendanceCenterWisePage } from '@/pages/org/OrgParentAttendanceCenterWisePage';
import { OrgSubjectTeacherCountPage } from '@/pages/org/OrgSubjectTeacherCountPage';
import { OrgCourseRegistrationsPage } from '@/pages/org/OrgCourseRegistrationsPage';
import { OrgVideoViewReportPage } from '@/pages/org/OrgVideoViewReportPage';
import { OrgAffidavitsPage } from '@/pages/org/OrgAffidavitsPage';
import { OrgDataExportPage } from '@/pages/org/OrgDataExportPage';
import { OrgLessonsPage } from '@/pages/org/OrgLessonsPage';
import { OrgLessonTopicsPage } from '@/pages/org/OrgLessonTopicsPage';
import { OrgBulkPromotionPage } from '@/pages/org/OrgBulkPromotionPage';
import { OrgPromotionReportPage } from '@/pages/org/OrgPromotionReportPage';
import { OrgPromoteStudentPage } from '@/pages/org/OrgPromoteStudentPage';
import { OrgCoScholasticPage } from '@/pages/org/OrgCoScholasticPage';
import { OrgSessionYearsPage } from '@/pages/org/OrgSessionYearsPage';
import { OrgClassChangeRequestsPage } from '@/pages/org/OrgClassChangeRequestsPage';
import { OrgTeacherClassChangeRequestsPage } from '@/pages/org/OrgTeacherClassChangeRequestsPage';
import { OrgUsageHistoryPage } from '@/pages/org/OrgUsageHistoryPage';
import { OrgLayout } from '@/components/org/OrgLayout';
import { AccountLoginPage } from '@/pages/account/AccountLoginPage';
import { OverviewPage } from '@/pages/superadmin/OverviewPage';
import { PlansPage } from '@/pages/superadmin/PlansPage';
import { ProgramDashboardPage } from '@/pages/superadmin/ProgramDashboardPage';
import { ProgramItemsPage } from '@/pages/superadmin/ProgramItemsPage';
import { ChestNumberRangesPage } from '@/pages/org/ChestNumberRangesPage';
import { SchoolsPage } from '@/pages/superadmin/SchoolsPage';
import { AdminUsersPage } from '@/pages/superadmin/AdminUsersPage';
import { OrganizationsPage } from '@/pages/superadmin/OrganizationsPage';
import { OrganizationDetailPage } from '@/pages/superadmin/OrganizationDetailPage';
import { BranchesPage } from '@/pages/superadmin/BranchesPage';
import { GlobalAdmissionLimitsPage } from '@/pages/superadmin/GlobalAdmissionLimitsPage';
import { SubscriptionsPage } from '@/pages/superadmin/SubscriptionsPage';
import { BiometricDevicesPage as SaBiometricDevicesPage } from '@/pages/superadmin/BiometricDevicesPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const schoolSlug = useAuthStore((s) => s.schoolSlug);
  const { ctx, loaded } = usePermissions();
  const { pathname } = useLocation();
  // School pages need a tenant context. A session without a schoolSlug (e.g. a
  // superadmin login, or a stale session) can't send the X-School-Code header,
  // so bounce it to the school login instead of rendering pages that will fail.
  if (!isAuthenticated || !schoolSlug) {
    return <Navigate to="/login" replace />;
  }
  // Wait for permissions before gating non-dashboard pages (avoid a flash-redirect).
  if (!loaded && pathname !== '/dashboard' && pathname !== '/') {
    return null;
  }
  // Permission-based access: redirect to the dashboard if this page isn't permitted.
  if (!canAccessPath(ctx, pathname)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

function SuperadminRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  if (!isAuthenticated) {
    return <Navigate to="/superadmin/login" replace />;
  }
  // scope is the real tier boundary — role alone isn't trustworthy here.
  // `organization_admins.role` reuses the SAME string labels
  // ('superadmin'/'support') for an unrelated, org-internal distinction —
  // 4 real org-admin accounts have role='superadmin' today, and without
  // this scope check they'd render this console despite every real API
  // call correctly 403ing (backend guards check scope, not role). See
  // docs/DEVIATIONS.md.
  if (
    user?.scope !== 'superadmin' ||
    (user.role !== 'superadmin' && user.role !== 'support' && user.role !== 'finance')
  ) {
    return <Navigate to="/superadmin/login" replace />;
  }
  return <>{children}</>;
}

function OrgRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const orgToken = useAuthStore((s) => s.orgToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  if (!isAuthenticated) {
    return <Navigate to="/org/login" replace />;
  }
  if (user?.scope === 'organization') {
    return <>{children}</>;
  }
  // A tenant session that still holds org origin context is mid-transition
  // (an org admin entering one of its schools). Don't bounce to /org/login —
  // render nothing and let the in-flight navigate('/dashboard') win.
  if (orgToken) {
    return null;
  }
  return <Navigate to="/org/login" replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Student / Parent PIN portal */}
      <Route path="/portal/login" element={<PortalLoginPage />} />
      <Route path="/portal" element={<PortalHomePage />} />

      {/* Guardian portal (username+password, spans schools) */}
      <Route path="/guardian/login" element={<GuardianLoginPage />} />
      <Route path="/guardian" element={<GuardianChildrenPage />} />

      {/* Organization admin portal */}
      <Route path="/org/login" element={<OrgLoginPage />} />
      <Route
        path="/org/select-school"
        element={
          <OrgRoute>
            <OrgSelectSchoolPage />
          </OrgRoute>
        }
      />
      <Route
        path="/org"
        element={
          <OrgRoute>
            <OrgLayout />
          </OrgRoute>
        }
      >
        <Route index element={<OrgOverviewPage />} />
        <Route path="settings" element={<PlatformSettingsPage readOnly />} />
        <Route path="admission-limits" element={<GlobalAdmissionLimitsPage readOnly />} />
        <Route path="schools" element={<OrgDashboardPage />} />
        <Route path="admin-users" element={<OrgAdminUsersPage />} />
        <Route path="students" element={<Navigate to="reports/students" replace />} />
        <Route path="students/guardians" element={<OrgGuardiansPage />} />
        <Route path="students/pgc-registrations" element={<OrgPgcRegistrationsPage />} />
        <Route path="affidavit" element={<OrgAffidavitsManagePage />} />
        <Route path="staff-management/staff" element={<OrgStaffPage />} />
        <Route path="staff-management/roles" element={<OrgRolesPage />} />
        <Route path="staff-management/staff-leave" element={<OrgStaffLeavePage />} />
        <Route path="staff-management/leave-report" element={<OrgLeaveReportPage />} />
        <Route path="online-admission/applicants" element={<OrgOnlineAdmissionsPage />} />
        <Route path="online-admission/summary" element={<OrgOnlineAdmissionsSummaryPage />} />
        <Route path="online-admission/classes" element={<OrgManageOnlineAdmissionClassesPage />} />
        <Route path="promotion/promote-student" element={<OrgPromoteStudentPage />} />
        <Route path="class-change-requests" element={<OrgClassChangeRequestsPage />} />
        <Route path="teacher-class-change-requests" element={<OrgTeacherClassChangeRequestsPage />} />
        <Route path="promotion/bulk" element={<OrgBulkPromotionPage />} />
        <Route path="promotion/report" element={<OrgPromotionReportPage />} />
        <Route path="co-scholastic" element={<OrgCoScholasticPage />} />
        <Route path="session-years" element={<OrgSessionYearsPage />} />
        {/* Module 4 — Academic dead-data stubs. Sections, Classes, Timetable,
            and Attendance are real but school-tier only — reached via the
            "enter a school" flow, not a route here. Academic Calendar has
            its own org-tier read-only aggregate route below. */}
        <Route
          path="academic/roll-numbers"
          element={<PagePlaceholder title="Assign Roll Numbers" description="Coming soon — no dedicated screen exists yet (roll number is currently a per-student field only)." />}
        />
        <Route
          path="academic/assignments"
          element={<PagePlaceholder title="Student Assignment" description="Coming soon — assignment data was never migrated." />}
        />
        <Route
          path="academic/online-exam"
          element={<PagePlaceholder title="Online Exam" description="Coming soon — online exam data was never migrated." />}
        />
        <Route
          path="academic/co-scholastic"
          element={<PagePlaceholder title="Co-scholastic Assessment" description="Coming soon — this data was never migrated." />}
        />
        <Route
          path="academic/offline-exam"
          element={<PagePlaceholder title="Offline Exam" description="Coming soon — exam data was never migrated (dead against real schools)." />}
        />
        <Route path="academic-calendar" element={<OrgAcademicCalendarPage />} />
        <Route path="mis-reports" element={<MisReportsPage scope="organization" />} />
        <Route path="reports/students" element={<StudentsReportPage />} />
        <Route path="reports/export" element={<OrgDataExportPage />} />
        <Route path="reports" element={<OrgReportsDashboardPage />} />
        <Route path="reports/category/:slug" element={<OrgReportsDashboardPage />} />
        <Route path="reports/departments" element={<OrgDepartmentsPage />} />
        <Route path="reports/lessons" element={<OrgLessonsPage />} />
        <Route path="reports/lesson-topics" element={<OrgLessonTopicsPage />} />
        <Route path="reports/finance" element={<OrgFinanceReportPage />} />
        <Route path="reports/login" element={<OrgLoginReportPage />} />
        <Route path="reports/teachers" element={<OrgTeachersReportPage />} />
        <Route path="reports/students/portfolio" element={<OrgStudentPortfolioPage />} />
        <Route path="reports/students-attendance-summary" element={<OrgStudentsAttendanceSummaryPage />} />
        <Route path="reports/teachers-attendance-summary" element={<OrgTeachersAttendanceSummaryPage />} />
        <Route path="reports/student-attendance" element={<OrgStudentAttendanceReportPage />} />
        <Route path="reports/teacher-attendance" element={<OrgTeacherAttendanceReportPage />} />
        <Route path="reports/detailed-student-attendance" element={<OrgDetailedStudentAttendancePage />} />
        <Route path="reports/detailed-teacher-attendance" element={<OrgDetailedTeacherAttendancePage />} />
        <Route path="reports/student-summary" element={<OrgStudentSummaryReportPage />} />
        <Route path="reports/parent-attendance-center-wise" element={<OrgParentAttendanceCenterWisePage />} />
        <Route path="reports/subject-teacher-count" element={<OrgSubjectTeacherCountPage />} />
        <Route path="reports/course-registrations" element={<OrgCourseRegistrationsPage />} />
        <Route path="reports/video-view" element={<OrgVideoViewReportPage />} />
        <Route path="reports/affidavits" element={<OrgAffidavitsPage />} />
        <Route path="reports/usage-logs" element={<OrgUsageHistoryPage />} />
        <Route path="program-dashboard" element={<ProgramDashboardPage />} />
        <Route path="program-items" element={<ProgramItemsPage />} />
        <Route path="chest-number-ranges" element={<ChestNumberRangesPage />} />
        <Route path="id-cards/generate" element={<GenerateIdCardsPage />} />
        <Route path="noorani-groups" element={<NooraniGroupsPage scope="organization" />} />
        <Route path="noorani-attendance" element={<NooraniAttendanceReportPage scope="organization" />} />
        <Route path="noorani-attendance/teachers" element={<NooraniTeacherAttendancePage scope="organization" />} />
        <Route path="noorani-tutorials" element={<NooraniTutorialsPage scope="organization" />} />
        <Route path="ho-fees-share" element={<HoFeesSharePage scope="organization" />} />
        <Route path="fees/type" element={<OrgFeeTypesPage />} />
        <Route path="fees/school-summary" element={<OrgSchoolFeesSummaryPage />} />
        <Route path="fees/head-office-overview" element={<OrgHeadOfficeFeesOverviewPage />} />
        <Route path="fees/manage" element={<FeesPage scope="organization" />} />
        <Route path="fees/noorani" element={<NooraniFeesPage scope="organization" />} />
        <Route path="fees/noorani/report" element={<NooraniFeeReportPage scope="organization" />} />
        <Route path="material-requests" element={<MaterialRequestsPage scope="organization" />} />
        <Route path="material-items" element={<MaterialItemsPage scope="organization" />} />
        <Route path="sliders" element={<OrgSlidersPage />} />
        <Route path="notifications" element={<OrgNotificationsPage />} />
        <Route path="quran-tracker/targets" element={<QuranTargetsPage scope="organization" />} />
        <Route path="quran-tracker/student-targets" element={<QuranStudentTargetsPage scope="organization" />} />
        <Route path="guardian-attendance" element={<ParentAttendancePage scope="organization" />} />
        <Route path="guardian-attendance/create" element={<Navigate to="/org/guardian-attendance" replace />} />
        <Route path="guardian-attendance/view" element={<Navigate to="/org/guardian-attendance" replace />} />
        <Route path="guardian-attendance/summary" element={<ParentAttendanceSummaryPage scope="organization" />} />
        <Route path="mobile-api-docs" element={<MobileApiDocsPage />} />
      </Route>

      {/* Superadmin portal */}
      <Route path="/superadmin/login" element={<SuperadminLoginPage />} />
      <Route
        path="/superadmin"
        element={
          <SuperadminRoute>
            <SuperadminLayout />
          </SuperadminRoute>
        }
      >
        <Route index element={<OverviewPage />} />
        <Route path="organizations" element={<OrganizationsPage />} />
        <Route path="organizations/:id" element={<OrganizationDetailPage />} />
        <Route path="schools" element={<SchoolsPage />} />
        <Route path="admin-users" element={<AdminUsersPage />} />
        <Route path="branches" element={<BranchesPage />} />
        <Route path="biometric-devices" element={<SaBiometricDevicesPage />} />
        <Route path="material-items" element={<MaterialItemsPage />} />
        <Route path="plans" element={<PlansPage />} />
        <Route path="subscriptions" element={<SubscriptionsPage />} />
        <Route path="online-admission/limits" element={<GlobalAdmissionLimitsPage />} />
        <Route path="program-items" element={<ProgramItemsPage />} />
        <Route path="program-dashboard" element={<ProgramDashboardPage />} />
        <Route
          path="admins"
          element={
            <PagePlaceholder
              title="Admins"
              description="Manage superadmin, support, and finance users."
            />
          }
        />
        <Route path="settings" element={<PlatformSettingsPage />} />
      </Route>

      {/* Public marketing site */}
      <Route path="/" element={<PublicLayout />}>
        <Route index element={<LandingPage />} />
        <Route path="pricing" element={<PricingPage />} />
        <Route path="signup" element={<SignupPage />} />
      </Route>

      {/* Public content pages (Privacy Policy, Terms & Conditions, etc.) — the "Public URL" shown in Content Pages editor. No auth, no PublicLayout chrome (matches real legacy's own standalone page view). */}
      <Route path="/page/:key" element={<PublicContentPage />} />

      {/* Online Admission — public application form, no auth. ONE shared URL
          for every school (matches legacy exactly) — the applicant picks
          their "Center" inside the form, not via a per-school URL segment.
          Distinct from the school-tier "/online-admissions" (plural) queue
          route below, which lives inside the authenticated layout. */}
      <Route path="/online-admission" element={<OnlineAdmissionApplyPage />} />

      {/* Multi-school account login (one login → many schools) */}
      <Route path="/account/login" element={<AccountLoginPage />} />

      {/* School-tenant portal */}
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <ProtectedRoute>
            <SchoolLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<DashboardPage />} />

        {/* Academic setup */}
        <Route path="setup/academic-years" element={<AcademicYearsPage />} />
        <Route path="setup/classes" element={<ClassesPage />} />
        <Route path="setup/class-subjects" element={<ClassSubjectsPage />} />
        <Route path="setup/section-teachers" element={<SectionTeachersPage />} />
        <Route path="setup/subjects" element={<SubjectsPage />} />
        <Route path="setup/lessons" element={<LessonsPage />} />
        <Route path="setup/custom-fields" element={<CustomFieldsPage />} />
        <Route path="promotion" element={<PromotionPage />} />
        <Route path="academic-calendar" element={<AcademicCalendarPage />} />

        {/* People */}
        <Route path="students" element={<StudentsPage />} />
        <Route path="students/new" element={<StudentRegistrationPage />} />
        {/* Alias for real legacy's exact route (`Route::resource('students', ...)` → GET /students/create) — same page as /students/new, not a duplicate. */}
        <Route path="students/create" element={<StudentRegistrationPage />} />
        <Route path="students/transfer" element={<TransferStudentsPage />} />
        <Route path="students/class-change-requests" element={<ClassChangeRequestsPage />} />
        <Route
          path="students/:id/edit"
          element={<StudentRegistrationPage />}
        />
        <Route path="students/:id" element={<StudentViewPage />} />
        <Route
          path="transfer-certificates"
          element={<TransferCertificatesPage />}
        />
        <Route path="staff" element={<StaffPage />} />
        <Route path="staff/class-change-requests" element={<TeacherClassChangeRequestsPage />} />
        <Route path="staff/:id" element={<StaffViewPage />} />
        <Route path="leave-management" element={<StaffLeavePage />} />
        <Route path="leave-management/report" element={<LeaveReportPage />} />
        <Route path="parents" element={<ParentsPage />} />
        <Route path="id-cards/verify" element={<VerifyIdCardsPage />} />
        <Route path="online-admissions" element={<OnlineAdmissionsPage />} />

        {/* Operations */}
        <Route path="visitors" element={<VisitorsPage />} />
        <Route path="pgc-registrations" element={<PgcRegistrationsPage />} />
        <Route path="visits" element={<VisitsPage />} />
        <Route path="attendance" element={<AttendancePage />} />
        <Route path="attendance/view" element={<ViewAttendancePage />} />
        <Route path="attendance/summary" element={<AttendanceSummaryPage />} />
        <Route path="attendance/staff" element={<StaffAttendancePage />} />
        <Route path="attendance/teachers/summary" element={<TeacherAttendanceSummaryPage />} />
        <Route path="attendance/admins" element={<AdminAttendancePage />} />
        <Route path="guardian-attendance" element={<ParentAttendancePage scope="school" />} />
        <Route path="guardian-attendance/create" element={<Navigate to="/guardian-attendance" replace />} />
        <Route path="guardian-attendance/view" element={<Navigate to="/guardian-attendance" replace />} />
        <Route path="guardian-attendance/summary" element={<ParentAttendanceSummaryPage scope="school" />} />
        <Route path="exams" element={<ExamsPage />} />
        <Route path="assignments" element={<AssignmentsPage />} />
        <Route path="co-scholastic" element={<CoScholasticPage />} />
        <Route path="program-candidates" element={<ProgramCandidatesPage />} />
        <Route path="online-exams" element={<OnlineExamsPage />} />
        <Route path="online-exams/question-bank" element={<QuestionBankPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="announcements" element={<AnnouncementsPage />} />
        <Route path="sliders" element={<SlidersPage />} />
        <Route path="affidavits" element={<AffidavitsPage />} />
        <Route path="fees" element={<FeesPage scope="school" />} />
        <Route path="fees/adjustment" element={<FeesAdjustmentPage />} />
        <Route path="fees/demands" element={<FeesDemandsPage />} />
        <Route path="fees/demands/new" element={<FeeDemandFormPage />} />
        <Route path="fees/demands/:id/edit" element={<FeeDemandFormPage />} />
        <Route path="fees/noorani" element={<NooraniFeesPage scope="school" />} />
        <Route path="fees/noorani/report" element={<NooraniFeeReportPage scope="school" />} />
        <Route path="expense-categories" element={<ExpenseCategoriesPage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="timetable" element={<TimetablePage />} />
        <Route path="timetable/teacher" element={<TeacherTimetablePage />} />
        <Route path="biometric-devices" element={<BiometricDevicesPage />} />

        {/* Modules */}
        <Route
          path="library"
          element={<PagePlaceholder title="Library" />}
        />
        <Route
          path="transport"
          element={<PagePlaceholder title="Transport" />}
        />
        <Route
          path="hostel"
          element={<PagePlaceholder title="Hostel" />}
        />

        {/* Reports */}
        <Route path="reports/login" element={<LoginReportPage />} />
        <Route path="reports/export" element={<DataExportPage />} />
        <Route path="reports/students/summary" element={<StudentSummaryReportPage />} />
        <Route path="reports/students" element={<SchoolStudentsReportPage />} />
        <Route path="reports/students/portfolio" element={<StudentPortfolioPage />} />
        <Route path="reports/teachers" element={<TeachersReportPage />} />
        <Route path="reports/finance" element={<FinanceReportPage />} />
        <Route path="reports/video-view" element={<VideoViewReportPage />} />
        <Route path="reports/attendance/students" element={<StudentAttendanceReportPage />} />
        <Route path="reports/attendance/students/summary" element={<AttendanceSummaryPage />} />
        <Route path="reports/attendance/students/detailed" element={<DetailedStudentAttendancePage />} />
        <Route path="reports/attendance/teachers" element={<TeacherAttendanceReportPage />} />
        <Route path="reports/attendance/teachers/detailed" element={<DetailedTeacherAttendancePage />} />
        <Route path="report-cards" element={<ReportCardPage />} />

        {/* System */}
        <Route path="mis-reports" element={<MisReportsPage scope="school" />} />
        <Route path="noorani-attendance" element={<NooraniAttendanceReportPage scope="school" />} />
        <Route path="noorani-attendance/teachers" element={<NooraniTeacherAttendancePage scope="school" />} />
        <Route path="noorani-tutorials" element={<NooraniTutorialsPage scope="school" />} />
        <Route path="ho-fees-share" element={<HoFeesSharePage scope="school" />} />
        <Route path="quran-tracker/targets" element={<QuranTargetsPage />} />
        <Route path="quran-tracker/student-targets" element={<QuranStudentTargetsPage />} />
        <Route path="material-items" element={<MaterialItemsPage scope="school" />} />
        <Route path="material-requests" element={<MaterialRequestsPage scope="school" />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="usage-history" element={<UsageHistoryPage />} />
        <Route path="usage-history/:userId" element={<UserUsageHistoryPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
